import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./api";
import { AdminAnnouncements } from "./components/AdminAnnouncements";
import { AdminReports } from "./components/AdminReports";
import { AdminRestrictedUsers } from "./components/AdminRestrictedUsers";
import { AdminVideos } from "./components/AdminVideos";
import { haptic, initializeTelegram } from "./telegram";
import { goBack, useBackNavigation } from "./use-back-navigation";

type ModerationPhoto = {
  photoId: string;
  userId: string;
  imageUrl: string;
  displayName: string;
  age: number;
  city: string;
  submittedAt: string;
};

type QueueResponse = {
  items: ModerationPhoto[];
  nextCursor: string | null;
};

type Summary = {
  pendingPhotos: number;
  pendingVideos: number;
  openReports: number;
  restrictedAccounts: number;
};

type RejectReason =
  | "not_a_person"
  | "face_not_visible"
  | "multiple_people"
  | "explicit_content"
  | "text_or_contact_info"
  | "low_quality"
  | "other";

const rejectReasons: Array<{ value: RejectReason; label: string }> = [
  { value: "not_a_person", label: "На фото нет человека" },
  { value: "face_not_visible", label: "Лицо плохо видно" },
  { value: "multiple_people", label: "Непонятно, кто владелец профиля" },
  { value: "explicit_content", label: "Недопустимый контент" },
  { value: "text_or_contact_info", label: "Текст или контакты на фото" },
  { value: "low_quality", label: "Слишком низкое качество" },
  { value: "other", label: "Другая причина" },
];

export function AdminApp() {
  const [status, setStatus] = useState<"booting" | "ready" | "telegram-required" | "forbidden" | "error">("booting");
  const [role, setRole] = useState("");
  const [tab, setTab] = useState<"photos" | "videos" | "reports" | "accounts" | "announcements">("photos");
  const [queue, setQueue] = useState<ModerationPhoto[]>([]);
  const [summary, setSummary] = useState<Summary>({
    pendingPhotos: 0,
    pendingVideos: 0,
    openReports: 0,
    restrictedAccounts: 0,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reasonCode, setReasonCode] = useState<RejectReason>("face_not_visible");
  const [note, setNote] = useState("");

  const loadModeration = useCallback(async () => {
    const [queueResult, summaryResult] = await Promise.all([
      api<QueueResponse>("/v1/admin/moderation/photos?limit=20"),
      api<Summary>("/v1/admin/moderation/summary"),
    ]);
    setQueue(queueResult.items);
    setSummary(summaryResult);
  }, []);

  useEffect(() => {
    const webApp = initializeTelegram();
    if (!webApp?.initData) {
      setStatus("telegram-required");
      return;
    }

    void (async () => {
      try {
        await api("/v1/auth/telegram", {
          method: "POST",
          body: JSON.stringify({ initData: webApp.initData }),
        });
        const admin = await api<{ role: string }>("/v1/admin/me");
        setRole(admin.role);
        await loadModeration();
        setStatus("ready");
      } catch (error) {
        if (error instanceof ApiError && error.status === 403) {
          setStatus("forbidden");
          return;
        }
        setStatus("error");
      }
    })();
  }, [loadModeration]);

  const back = () => {
    if (busy) return;
    if (rejecting) setRejecting(false);
    else if (tab !== "photos") setTab("photos");
    else location.assign("/");
  };
  useBackNavigation(back, !busy);
  const refresh = async () => {
    setError("");
    try {
      await loadModeration();
    } catch {
      setError("Не удалось обновить очередь. Попробуйте ещё раз.");
    }
  };
  const current = queue[0];

  const approve = async () => {
    if (!current || busy) return;
    setBusy(true);
    setError("");
    try {
      await api(`/v1/admin/moderation/photos/${current.photoId}/decision`, {
        method: "POST",
        body: JSON.stringify({ decision: "approve" }),
      });
      haptic("success");
      setRejecting(false);
      setQueue((items) => items.filter((item) => item.photoId !== current.photoId));
      await refresh();
    } catch {
      setError("Не удалось сохранить решение. Проверьте соединение и повторите попытку.");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!current || busy) return;
    setBusy(true);
    setError("");
    try {
      await api(`/v1/admin/moderation/photos/${current.photoId}/decision`, {
        method: "POST",
        body: JSON.stringify({
          decision: "reject",
          reasonCode,
          ...(note.trim() ? { note: note.trim() } : {}),
        }),
      });
      haptic("pass");
      setRejecting(false);
      setNote("");
      setQueue((items) => items.filter((item) => item.photoId !== current.photoId));
      await refresh();
    } catch {
      setError("Не удалось сохранить решение. Проверьте соединение и повторите попытку.");
    } finally {
      setBusy(false);
    }
  };

  const feedback = error && (
    <div className="reference-error" role="alert">
      {error}
      <button type="button" className="secondary-button" disabled={busy} onClick={() => void refresh()}>
        Обновить очередь
      </button>
    </div>
  );

  if (status === "booting")
    return (
      <main className="admin-system-screen">
        <div className="brand-mark">D</div>
        <p>Загружаем очередь модерации…</p>
      </main>
    );

  if (status === "telegram-required")
    return (
      <main className="admin-system-screen">
        <h1>Откройте панель в Telegram</h1>
        <p>Войдите в панель через DonDate в Telegram.</p>
      </main>
    );

  if (status === "forbidden")
    return (
      <main className="admin-system-screen">
        <h1>Нет доступа</h1>
        <p>Для этого аккаунта не назначена активная роль модератора.</p>
      </main>
    );

  if (status === "error")
    return (
      <main className="admin-system-screen">
        <h1>Ошибка панели</h1>
        <p>Не удалось загрузить данные модерации.</p>
        <button type="button" className="secondary-button" onClick={() => location.reload()}>
          Повторить
        </button>
        <a className="section-back" href="/">
          Вернуться в приложение
        </a>
      </main>
    );

  return (
    <main className="admin-shell">
      <button type="button" className="section-back" disabled={busy} onClick={goBack}>
        {tab === "photos" && !rejecting ? "‹ В приложение" : "‹ Назад"}
      </button>
      {(tab !== "photos" || !current) && feedback}
      <header className="admin-header">
        <div>
          <span className="eyebrow">DonDate moderation</span>
          <h1>
            {tab === "announcements"
              ? "Объявления команды"
              : tab === "photos"
                ? "Проверка фото"
                : tab === "videos"
                  ? "Проверка видео"
                  : tab === "reports"
                    ? "Жалобы"
                    : "Ограничения"}
          </h1>
        </div>
        <span className="admin-role">{role}</span>
      </header>

      <section className="admin-stats" aria-label="Очередь модерации">
        <button type="button" className={tab === "photos" ? "active" : ""} onClick={() => setTab("photos")}>
          <strong>{summary.pendingPhotos}</strong>
          <span>фото ожидают</span>
        </button>
        <button type="button" className={tab === "videos" ? "active" : ""} onClick={() => setTab("videos")}>
          <strong>{summary.pendingVideos ?? 0}</strong>
          <span>видео ожидают</span>
        </button>
        <button type="button" className={tab === "reports" ? "active" : ""} onClick={() => setTab("reports")}>
          <strong>{summary.openReports}</strong>
          <span>открытых жалоб</span>
        </button>
        <button type="button" className={tab === "accounts" ? "active" : ""} onClick={() => setTab("accounts")}>
          <strong>{summary.restrictedAccounts}</strong>
          <span>ограниченных</span>
        </button>
      </section>

      {(role === "admin" || role === "super_admin") && (
        <button
          type="button"
          className="reference-row admin-announcements-link"
          aria-pressed={tab === "announcements"}
          onClick={() => setTab("announcements")}
        >
          Объявления команды
        </button>
      )}
      {tab === "announcements" ? (
        <AdminAnnouncements />
      ) : tab === "videos" ? (
        <AdminVideos onChanged={loadModeration} />
      ) : tab === "reports" ? (
        <AdminReports role={role} onChanged={loadModeration} />
      ) : tab === "accounts" ? (
        <AdminRestrictedUsers role={role} onChanged={loadModeration} />
      ) : !current ? (
        <section className="empty-state admin-empty">
          <div className="empty-orbit">✓</div>
          <h2>Очередь разобрана</h2>
          <p>Новых фотографий на проверку сейчас нет.</p>
          <button type="button" className="secondary-button" onClick={() => void refresh()}>
            Обновить
          </button>
        </section>
      ) : (
        <section className="moderation-card">
          <img src={current.imageUrl} alt={`Фото профиля ${current.displayName}`} />
          <div className="moderation-meta">
            <div>
              <h2>
                {current.displayName}, {current.age}
              </h2>
              <p>{current.city}</p>
            </div>
            <span>{new Date(current.submittedAt).toLocaleDateString("ru-RU")}</span>
          </div>

          {feedback}
          {rejecting ? (
            <div className="reject-panel">
              <label>
                <span>Причина отклонения</span>
                <select value={reasonCode} onChange={(event) => setReasonCode(event.target.value as RejectReason)}>
                  {rejectReasons.map((reason) => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>
                  Заметка модератора <em>необязательно</em>
                </span>
                <textarea
                  maxLength={500}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="Внутренняя заметка, пользователь её не увидит"
                />
              </label>
              <div className="admin-actions">
                <button type="button" className="secondary-button" disabled={busy} onClick={() => setRejecting(false)}>
                  Отмена
                </button>
                <button type="button" className="danger-button" disabled={busy} onClick={reject}>
                  {busy ? "Сохраняем…" : "Отклонить фото"}
                </button>
              </div>
            </div>
          ) : (
            <div className="admin-actions">
              <button type="button" className="danger-button" disabled={busy} onClick={() => setRejecting(true)}>
                Отклонить
              </button>
              <button type="button" className="approve-button" disabled={busy} onClick={approve}>
                {busy ? "Сохраняем…" : "Одобрить"}
              </button>
            </div>
          )}
        </section>
      )}
    </main>
  );
}
